import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Text,
  Button,
  Textarea,
  Divider,
  useToast,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase';
import UnifiedActivityTimeline from './UnifiedActivityTimeline';

/**
 * Notes view for a contact: quick contact-level note composer on top, then ALL
 * notes about this person — contact-level notes, notes from every deal (badged
 * with the deal name), AI call summaries, and dispositions — rendered through
 * UnifiedActivityTimeline preset to the note-like categories. Users can widen
 * the filters from there if they want the full activity stream.
 */
export default function ContactNotes({ contactId, dealIds = [] }) {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding] = useState(false);
  // Bumped after adds + realtime events so the timeline refetches
  const [refreshKey, setRefreshKey] = useState(0);

  // Live updates for contact-level rows (e.g. an AI call summary landing while
  // the drawer is open). Deal-row changes are covered by the refresh bump on add
  // and by reopening the drawer.
  useEffect(() => {
    if (!contactId) return;

    const subscription = supabase
      .channel(`contact-notes-${contactId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'crm_activities',
        filter: `contact_id=eq.${contactId}`
      }, () => {
        setRefreshKey((k) => k + 1);
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [contactId]);

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast({
        title: 'Note is empty',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    setAdding(true);
    try {
      const noteData = {
        contact_id: contactId,
        deal_id: null, // Contact-level note
        type: 'note',
        content: newNote.trim(),
        author_id: currentUser.uid,
        metadata: {
          authorName: currentUser.displayName || currentUser.email,
          authorPhotoURL: currentUser.photoURL || '',
          noteType: 'general',
        },
      };

      const { error } = await supabase
        .from('crm_activities')
        .insert(noteData);

      if (error) throw error;

      setRefreshKey((k) => k + 1);

      toast({
        title: 'Note added',
        status: 'success',
        duration: 2000,
      });

      setNewNote('');
    } catch (error) {
      console.error('Error adding note:', error);
      toast({
        title: 'Error adding note',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setAdding(false);
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      {/* Add Note Form */}
      <Box>
        <Text fontWeight="semibold" fontSize="sm" mb={2}>
          Add Contact Note
        </Text>
        <VStack spacing={2}>
          <Textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="General observations, preferences, relationship history..."
            rows={3}
            size="sm"
          />
          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<FiPlus />}
            onClick={handleAddNote}
            isLoading={adding}
            loadingText="Adding..."
            alignSelf="flex-end"
            isDisabled={!newNote.trim()}
          >
            Add Note
          </Button>
        </VStack>
        <Text fontSize="xs" color="gray.500" mt={1}>
          Saved on the contact (not tied to a deal). Deal notes below are badged with their deal.
        </Text>
      </Box>

      <Divider />

      {/* All notes about this person — contact notes + per-deal notes + AI call
          summaries, preset to note-like categories (filters can widen it) */}
      <UnifiedActivityTimeline
        contactId={contactId}
        dealIds={dealIds}
        maxHeight="500px"
        refreshKey={refreshKey}
        presetCategories={['notes', 'dispositions', 'calls']}
      />
    </VStack>
  );
}
